import React, { useState, useEffect } from 'react';
import { useNotificaciones } from '../hooks/useNotificaciones';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../servicios/firebase';

const ETIQUETAS_CANAL = {
  taquilla: '🏪 Taquilla',
  telefono: '📞 Telefónica',
  whatsapp: '📱 WhatsApp',
  web: '🌐 Venta en línea',
  otro: '🌐 Otro',
};

// Nombre(s) del/los pasajeros: primero el mapa pasajeros {asiento: nombre},
// si no existe cae al nombre de contacto (las ventas web lo traen en null).
const nombresPasajeros = (t) =>
  Object.values(t.pasajeros || {}).filter(Boolean).join(', ') || t.contacto?.nombre || '—';

const formatearHora = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

const ResumenTicket = ({ ticket }) => {
  const esRedondo = ticket.tipoViaje === 'redondo' || ticket.esRedondo;
  const origen = ticket.ruta?.origen || ticket.rutaIda?.origen || '—';
  const destino = ticket.ruta?.destino || ticket.rutaIda?.destino || '—';
  const fecha = ticket.fechaViaje || ticket.fechaIda || '—';
  const hora = ticket.horaViaje || ticket.horaIda || '—';
  const pasajeros = Object.keys(ticket.pasajeros || {}).length || ticket.asientosIda?.length || 1;
  const metodoPago = ticket.metodoPago === 'efectivo_taquilla' ? '💵 Taquilla' : '💳 Online';

  return (
    <div className="space-y-1.5 text-xs">
      <div className="flex items-center gap-2">
        <span className={`px-2 py-0.5 rounded-full font-black text-[10px] ${esRedondo ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
          {esRedondo ? '⮂ REDONDO' : '→ SENCILLO'}
        </span>
        <span className="text-gray-400">{metodoPago}</span>
      </div>
      <div className="font-bold text-gray-800">{origen} ➝ {destino}</div>
      <div className="text-gray-500">📅 {fecha} • ⏰ {hora} • 👤 {pasajeros} pax</div>
      <div className="text-gray-600 text-[11px] font-semibold">🧍 {nombresPasajeros(ticket)}</div>
      {esRedondo && ticket.fechaRegreso && (
        <div className="text-purple-600 text-[10px] font-bold">
          ↩ Regreso: {ticket.fechaRegreso} {ticket.horaRegreso ? `• ${ticket.horaRegreso}` : ''}
        </div>
      )}
      {ticket.contacto?.telefono && (
        <div className="text-gray-400 text-[10px]">📱 {ticket.contacto.telefono}</div>
      )}
    </div>
  );
};

const NotificacionesFlotante = () => {
  const { notificaciones, noLeidos, marcarLeido, marcarTodosLeidos, limpiarTodas } = useNotificaciones();
  const [abierto, setAbierto] = useState(false);
  const [expandido, setExpandido] = useState(null);
  const [pestana, setPestana] = useState('envivo'); // 'envivo' | 'historial'
  const navigate = useNavigate();

  // ── Historial por fecha ──────────────────────────────────────────────────
  const [fechaHistorial, setFechaHistorial] = useState(new Date().toISOString().split('T')[0]);
  const [historial, setHistorial] = useState([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [expandidoHistorial, setExpandidoHistorial] = useState(null);

  useEffect(() => {
    if (pestana !== 'historial' || !abierto) return;
    const cargar = async () => {
      setCargandoHistorial(true);
      try {
        const inicio = new Date(fechaHistorial + 'T00:00:00');
        const fin = new Date(fechaHistorial + 'T23:59:59');
        const q = query(
          collection(db, 'tickets'),
          where('fechaCompra', '>=', Timestamp.fromDate(inicio)),
          where('fechaCompra', '<=', Timestamp.fromDate(fin))
        );
        const snap = await getDocs(q);
        const lista = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const fa = a.fechaCompra?.toDate?.() || new Date(0);
            const fb = b.fechaCompra?.toDate?.() || new Date(0);
            return fb - fa; // más recientes primero
          });
        setHistorial(lista);
      } catch (e) {
        console.error('Error cargando historial:', e);
        setHistorial([]);
      } finally {
        setCargandoHistorial(false);
      }
    };
    cargar();
  }, [pestana, fechaHistorial, abierto]);

  if (notificaciones.length === 0 && !abierto) {
    return (
      <button
        onClick={() => setAbierto(true)}
        className="fixed bottom-6 right-6 z-[9999] bg-white border border-gray-200 shadow-lg rounded-full w-12 h-12 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-all hover:shadow-xl"
        title="Notificaciones de ventas"
      >
        🔔
      </button>
    );
  }

  return (
    <>
      {/* BOTÓN FLOTANTE */}
      <button
        onClick={() => { setAbierto(!abierto); if (!abierto) marcarTodosLeidos(); }}
        className={`fixed bottom-6 right-6 z-[9999] shadow-xl rounded-full w-14 h-14 flex items-center justify-center transition-all hover:scale-110 ${
          noLeidos > 0
            ? 'bg-emerald-500 hover:bg-emerald-600 animate-bounce'
            : 'bg-white border border-gray-200 hover:shadow-2xl'
        }`}
        title={noLeidos > 0 ? `${noLeidos} venta(s) nueva(s)` : 'Notificaciones'}
      >
        <span className="text-2xl">{noLeidos > 0 ? '🎟️' : '🔔'}</span>
        {noLeidos > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-black rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 shadow">
            {noLeidos > 9 ? '9+' : noLeidos}
          </span>
        )}
      </button>

      {/* PANEL */}
      {abierto && (
        <div className="fixed bottom-24 right-6 z-[9998] w-80 max-h-[75vh] flex flex-col bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">

          {/* HEADER */}
          <div className="bg-slate-800 px-4 py-3 flex justify-between items-center shrink-0">
            <h3 className="text-white font-black text-sm">🎟️ Ventas</h3>
            <div className="flex items-center gap-2">
              {pestana === 'envivo' && notificaciones.length > 0 && (
                <button onClick={limpiarTodas} className="text-slate-400 hover:text-red-400 text-[10px] font-bold transition-colors">
                  Limpiar
                </button>
              )}
              <button onClick={() => setAbierto(false)} className="text-slate-400 hover:text-white font-bold text-lg leading-none">✕</button>
            </div>
          </div>

          {/* PESTAÑAS */}
          <div className="flex shrink-0 bg-slate-700">
            <button
              onClick={() => setPestana('envivo')}
              className={`flex-1 py-2 text-[11px] font-bold transition-colors ${pestana === 'envivo' ? 'bg-white text-slate-800' : 'text-slate-300 hover:text-white'}`}
            >
              🟢 En vivo {noLeidos > 0 && <span className="ml-1 bg-red-500 text-white text-[9px] px-1.5 py-0.5 rounded-full">{noLeidos}</span>}
            </button>
            <button
              onClick={() => setPestana('historial')}
              className={`flex-1 py-2 text-[11px] font-bold transition-colors ${pestana === 'historial' ? 'bg-white text-slate-800' : 'text-slate-300 hover:text-white'}`}
            >
              📅 Historial
            </button>
          </div>

          {/* ── PESTAÑA EN VIVO ── */}
          {pestana === 'envivo' && (
            <div className="overflow-y-auto flex-1">
              {notificaciones.length === 0 ? (
                <div className="p-6 text-center text-gray-400">
                  <p className="text-2xl mb-2">🔕</p>
                  <p className="text-xs">Sin ventas desde que abriste el panel</p>
                  <p className="text-[10px] text-gray-300 mt-1">Usa la pestaña Historial para ver ventas anteriores</p>
                </div>
              ) : (
                notificaciones.map((notif) => (
                  <div key={notif.id} className={`border-b border-gray-50 transition-colors ${!notif.leido ? 'bg-emerald-50' : 'bg-white hover:bg-gray-50'}`}>
                    <div className="px-4 py-3 cursor-pointer" onClick={() => { marcarLeido(notif.id); setExpandido(expandido === notif.id ? null : notif.id); }}>
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            {!notif.leido && <span className="w-2 h-2 bg-emerald-500 rounded-full shrink-0"></span>}
                            <span className="text-xs font-black text-gray-800 truncate">
                              {notif.ruta?.origen || notif.rutaIda?.origen || '—'} ➝ {notif.ruta?.destino || notif.rutaIda?.destino || '—'}
                            </span>
                          </div>
                          <div className="text-[10px] text-gray-400 pl-3.5">
                            {notif.fechaViaje || notif.fechaIda || '—'} • {notif.horaViaje || notif.horaIda || '—'}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="text-[9px] text-gray-400">{formatearHora(notif.fechaCompra)}</span>
                          <span className="text-gray-300 text-xs">{expandido === notif.id ? '▲' : '▼'}</span>
                        </div>
                      </div>
                    </div>
                    {expandido === notif.id && (
                      <div className="px-4 pb-3 border-t border-gray-100 pt-2">
                        <ResumenTicket ticket={notif} />
                        <div className="mt-3 flex gap-2">
                          <button
                            onClick={() => {
                              setAbierto(false);
                              const viajeId = notif.viajeId || '';
                              const asientos = (notif.asientosIda || []).join(',');
                              const fecha = notif.fechaViaje || notif.fechaIda || '';
                              navigate(`/admin/salidas?viajeId=${viajeId}&asientos=${asientos}&fecha=${fecha}`);
                            }}
                            className="flex-1 py-1.5 bg-slate-800 hover:bg-slate-700 text-white text-[10px] font-bold rounded-lg transition-colors"
                          >
                            Ver en Taquilla →
                          </button>
                          <button
                            onClick={() => { setAbierto(false); navigate('/admin/reportes'); }}
                            className="flex-1 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-bold rounded-lg transition-colors"
                          >
                            Ver Reportes →
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── PESTAÑA HISTORIAL ── */}
          {pestana === 'historial' && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Selector de fecha */}
              <div className="px-3 py-2 border-b border-gray-100 shrink-0 flex items-center gap-2 bg-gray-50">
                <span className="text-[10px] font-bold text-gray-500">📅 Fecha:</span>
                <input
                  type="date"
                  value={fechaHistorial}
                  onChange={e => setFechaHistorial(e.target.value)}
                  className="flex-1 text-xs font-bold text-gray-700 border border-gray-200 rounded-lg px-2 py-1 outline-none focus:border-blue-400 bg-white"
                />
              </div>

              <div className="overflow-y-auto flex-1">
                {cargandoHistorial ? (
                  <div className="p-6 text-center text-gray-400 text-xs animate-pulse">Cargando ventas del día...</div>
                ) : historial.length === 0 ? (
                  <div className="p-6 text-center text-gray-400">
                    <p className="text-2xl mb-2">📭</p>
                    <p className="text-xs">Sin ventas ese día</p>
                  </div>
                ) : (
                  historial.map((ticket) => {
                    const cancelado = ticket.estado?.startsWith('cancelado');
                    const conReembolso = ticket.estado === 'cancelado_con_reembolso';
                    const origen = ticket.ruta?.origen || ticket.rutaIda?.origen || '—';
                    const destino = ticket.ruta?.destino || ticket.rutaIda?.destino || '—';
                    const horaCompra = ticket.fechaCompra?.toDate
                      ? ticket.fechaCompra.toDate().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
                      : '—';

                    return (
                      <div key={ticket.id} className={`border-b border-gray-50 ${cancelado ? 'bg-red-50' : 'bg-white hover:bg-gray-50'}`}>
                        <div
                          className="px-4 py-3 cursor-pointer"
                          onClick={() => setExpandidoHistorial(expandidoHistorial === ticket.id ? null : ticket.id)}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${ticket.tipoViaje === 'redondo' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                  {ticket.tipoViaje === 'redondo' ? '⮂' : '→'}
                                </span>
                                {cancelado && (
                                  <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${conReembolso ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`}>
                                    {conReembolso ? '💵 Reembolsado' : '🔓 Liberado'}
                                  </span>
                                )}
                                <span className="text-xs font-black text-gray-800 truncate">{origen} ➝ {destino}</span>
                              </div>
                              <div className="text-[10px] text-gray-400">
                                {nombresPasajeros(ticket)} • {ETIQUETAS_CANAL[ticket.canalVenta] || ticket.canalVenta || '—'}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <span className="text-[9px] text-gray-400">{horaCompra}</span>
                              <span className="text-gray-300 text-xs">{expandidoHistorial === ticket.id ? '▲' : '▼'}</span>
                            </div>
                          </div>
                        </div>

                        {expandidoHistorial === ticket.id && (
                          <div className="px-4 pb-3 border-t border-gray-100 pt-2 space-y-1 text-[10px] text-gray-600">
                            <p><span className="font-bold text-gray-400">Pasajero{Object.keys(ticket.pasajeros || {}).length > 1 ? 's' : ''}:</span> {nombresPasajeros(ticket)}</p>
                            <p><span className="font-bold text-gray-400">Teléfono:</span> {ticket.contacto?.telefono || '—'}</p>
                            <p><span className="font-bold text-gray-400">Ruta:</span> {origen} ➝ {destino}</p>
                            <p><span className="font-bold text-gray-400">Fecha viaje:</span> {ticket.fechaViaje || ticket.fechaIda || '—'} • {ticket.horaViaje || ticket.horaIda || '—'}</p>
                            <p><span className="font-bold text-gray-400">Asientos:</span> {(ticket.asientos || ticket.asientosIda || []).join(', ') || '—'}</p>
                            <p><span className="font-bold text-gray-400">Canal:</span> {ETIQUETAS_CANAL[ticket.canalVenta] || ticket.canalVenta || '—'}</p>
                            {ticket.atendidoPor && <p><span className="font-bold text-gray-400">Atendió:</span> {ticket.atendidoPor}</p>}
                            <p><span className="font-bold text-gray-400">Total:</span> <span className="font-black text-emerald-600">${ticket.precioTotal || 0}</span></p>
                            {cancelado && (
                              <>
                                <p><span className="font-bold text-gray-400">Motivo:</span> {ticket.motivoCancelacion || '—'}</p>
                                <p><span className="font-bold text-gray-400">Autorizó:</span> {ticket.canceladoPor || '—'}</p>
                                {conReembolso && <p><span className="font-bold text-gray-400">Reembolso:</span> <span className="font-black text-blue-600">${ticket.reembolso?.monto || 0}</span></p>}
                              </>
                            )}
                            <p><span className="font-bold text-gray-400">Folio:</span> {ticket.id}</p>
                            <div className="mt-2 flex gap-2">
                              <button
                                onClick={() => {
                                  setAbierto(false);
                                  const viajeId = ticket.viajeId || '';
                                  const asientos = (ticket.asientosIda || ticket.asientos || []).join(',');
                                  const fecha = ticket.fechaViaje || ticket.fechaIda || '';
                                  navigate(`/admin/salidas?viajeId=${viajeId}&asientos=${asientos}&fecha=${fecha}`);
                                }}
                                className="flex-1 py-1.5 bg-slate-800 hover:bg-slate-700 text-white text-[10px] font-bold rounded-lg transition-colors"
                              >
                                Ver en Taquilla →
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {historial.length > 0 && (
                <div className="shrink-0 px-4 py-2 bg-gray-50 border-t border-gray-100 text-[10px] text-gray-400 text-center">
                  {historial.length} venta(s) •{' '}
                  <span className="font-black text-emerald-600">
                    ${historial.filter(t => t.estado === 'pagado').reduce((s, t) => s + (t.precioTotal || 0), 0).toLocaleString()}
                  </span> cobrado
                  {historial.some(t => t.reembolso?.monto > 0) && (
                    <> • <span className="font-black text-blue-600">-${historial.reduce((s, t) => s + (t.reembolso?.monto || 0), 0).toLocaleString()}</span> reembolsado</>
                  )}
                </div>
              )}
            </div>
          )}

          {pestana === 'envivo' && notificaciones.length > 0 && (
            <div className="shrink-0 px-4 py-2 bg-gray-50 border-t border-gray-100">
              <p className="text-[10px] text-gray-400 text-center">
                <button onClick={marcarTodosLeidos} className="text-blue-500 hover:underline">Marcar todas leídas</button>
              </p>
            </div>
          )}
        </div>
      )}

      {abierto && <div className="fixed inset-0 z-[9997]" onClick={() => setAbierto(false)} />}
    </>
  );
};

export default NotificacionesFlotante;